import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Container,
  Dialog,
  Heading,
  Pressable,
  ProgressBar,
  Screen,
  Skeleton,
  Stack,
  Text,
  useToast,
} from '@fluent/ui';
import { EXAM_MAX_SCORE, EXAM_TASKS, type ExamTaskKey, type ExamTest } from '@fluent/types';
import { useAuth } from '../auth/authStore.js';
import { useProAccessState } from '../subscription/hooks.js';
import { EVERYTHING_FREE } from '../subscription/freeMode.js';
import { Paywall } from '../subscription/Paywall.js';
import { isFreeExamTest, levelOf, loadExamTest, numberOf } from '../exam/catalog.js';
import { useExamResultsSync, useReportExamTasks, useSaveExamResult } from '../exam/hooks.js';
import { useCelebrationCrossings } from '../progress/useCelebrationCrossings.js';
import { LevelUpModal } from '../lesson/LevelUpModal.js';
import {
  answeredCount,
  clearAnswer,
  finishAttempt,
  markReported,
  newlyFinishedTasks,
  normalizeAttempt,
  formatClock,
  newAttempt,
  remainingMs,
  scoreAnswers,
  setAnswer,
  unansweredTotal,
  type ExamAnswers,
  type ExamAttempt,
  type ExamResult,
  type ListeningState,
} from '../exam/attempt.js';
import { useExamStore, useUserExams } from '../exam/examStore.js';
import { useExamClock } from '../exam/useExamClock.js';
import { ListeningTask } from '../exam/ListeningTask.js';
import { DialogueTask, GrammarTask, MatchingTask, ReadingTask, VocabularyTask } from '../exam/tasks.js';
import '../exam/Exam.css';

/**
 * One mock test, start to finish: intro → the six tasks under a 2-hour
 * clock → results → review of every answer with its proof.
 *
 * A sitting in progress lives in the exam store (localStorage), so opening
 * the test again drops the learner straight back on the task and second
 * where they left (Tornike 2026-09-25).
 */

type View = 'intro' | 'running' | 'results' | 'review';

export interface ExamPlayerProps {
  demo?: boolean;
  basePath?: string;
}

const WARN_MS = 10 * 60 * 1000;
const DANGER_MS = 2 * 60 * 1000;

export function ExamPlayer({ demo = false, basePath = '/test' }: ExamPlayerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { testId = '' } = useParams();
  const { user } = useAuth();
  const realAccess = useProAccessState();
  // Tests 1–3 are free for everyone (FREE_EXAM_TESTS); the rest are Pro —
  // unless everything is free (freeMode.ts).
  const access = demo || EVERYTHING_FREE || isFreeExamTest(numberOf(testId)) ? 'yes' : realAccess;
  const userKey = demo ? 'demo' : (user?.id ?? '');
  const exams = useUserExams(userKey);
  const saveAttempt = useExamStore((s) => s.saveAttempt);
  const recordResult = useExamStore((s) => s.recordResult);
  const reportTasks = useReportExamTasks();
  const saveResult = useSaveExamResult();
  // Results follow the account: last/best on the intro come from any device.
  useExamResultsSync(!demo);
  const { toast } = useToast();
  /** XP the server paid for tasks finished in this sitting. */
  const [sittingXp, setSittingXp] = useState(0);

  const [test, setTest] = useState<ExamTest | null | undefined>(undefined);
  const [loadFailed, setLoadFailed] = useState(false);
  const [view, setView] = useState<View>(() => (exams.active[testId] ? 'running' : 'intro'));
  // Finishing tasks pays XP and extends the streak, so a sitting can cross a
  // level or a streak milestone — celebrated on the results screen.
  const { levelUpPending, streakMilestone, level, clearLevelUp } = useCelebrationCrossings(view === 'results');
  const [shownResult, setShownResult] = useState<ExamResult | null>(null);

  const load = useCallback(() => {
    setLoadFailed(false);
    setTest(undefined);
    loadExamTest(testId).then(setTest, () => setLoadFailed(true));
  }, [testId]);
  useEffect(load, [load]);

  const n = numberOf(testId) ?? 0;
  const back = () => navigate(basePath);

  if (loadFailed) {
    return (
      <Screen>
        <Container size="sm">
          <Stack gap={4} align="center" style={{ padding: 'var(--space-16, 64px) 0', textAlign: 'center' }}>
            <Heading level={3}>{t('exam.load_error_title')}</Heading>
            <Text tone="muted">{t('exam.load_error_body')}</Text>
            <Button variant="secondary" onClick={load}>
              {t('exam.listen_retry')}
            </Button>
          </Stack>
        </Container>
      </Screen>
    );
  }
  if (test === undefined || access === 'loading') {
    return (
      <Screen>
        <Container size="sm">
          <Stack gap={4} style={{ padding: 'var(--space-6, 24px) 0' }}>
            <Skeleton shape="card" style={{ height: 320 }} />
          </Stack>
        </Container>
      </Screen>
    );
  }
  if (test === null) {
    return (
      <Screen>
        <Container size="sm">
          <Stack gap={4} align="center" style={{ padding: 'var(--space-16, 64px) 0' }}>
            <Text tone="muted">{t('exam.not_found')}</Text>
            <Button variant="secondary" onClick={back}>
              {t('exam.back_to_tests')}
            </Button>
          </Stack>
        </Container>
      </Screen>
    );
  }
  if (access === 'no') return <Paywall />;

  const results = exams.results[testId];

  if (view === 'running') {
    return (
      <RunningExam
        key={testId}
        test={test}
        initial={exams.active[testId] ? normalizeAttempt(exams.active[testId]) : newAttempt(testId, Date.now())}
        onSave={(a) => saveAttempt(userKey, a)}
        // The demo route has no account: nothing to send.
        onTasksFinished={
          demo
            ? undefined
            : (id, tasks) =>
                reportTasks(id, n, tasks, (r) => {
                  if (r.xp_awarded <= 0) return;
                  setSittingXp((x) => x + r.xp_awarded);
                  toast({ tone: 'success', message: t('exam.task_xp', { xp: r.xp_awarded }), duration: 2500 });
                })
        }
        onFinish={(r) => {
          recordResult(userKey, r);
          if (!demo) saveResult(r);
          setShownResult(r);
          setView('results');
          window.scrollTo(0, 0);
        }}
        onExit={back}
      />
    );
  }

  if (view === 'results' && shownResult) {
    // A crossed level is celebrated first, then the score.
    if (levelUpPending) return <LevelUpModal level={level} onContinue={clearLevelUp} />;
    return (
      <ExamResults
        n={n}
        result={shownResult}
        xp={sittingXp}
        streakMilestone={streakMilestone}
        onReview={() => {
          setView('review');
          window.scrollTo(0, 0);
        }}
        onRetake={() => setView('intro')}
        onBack={back}
      />
    );
  }

  if (view === 'review' && shownResult) {
    return <ExamReview test={test} answers={shownResult.answers} onClose={() => setView('results')} />;
  }

  // Intro.
  return (
    <Screen>
      <Container size="sm">
        <Stack gap={6} style={{ padding: 'var(--space-6, 24px) 0 var(--space-16, 64px)' }}>
          <div>
            <Button variant="ghost" size="sm" onClick={back}>
              ← {t('exam.back_to_tests')}
            </Button>
          </div>
          <div className="fluent-exam__intro">
            <p className="fluent-exam__intro-kicker">{t(`exam.level_${levelOf(n)}`)}</p>
            <Heading level={1}>{t('exam.test_n', { n })}</Heading>
            <Text weight="bold" tone="muted">
              {t('exam.intro_meta')}
            </Text>
            <ol className="fluent-exam__info-tasks">
              {EXAM_TASKS.map((task, i) => (
                <li key={task.key}>
                  <span>
                    {i + 1}. {t(`exam.task_${i + 1}`)}
                  </span>
                  <span className="fluent-exam__info-pts">{t('exam.task_points', { n: task.items })}</span>
                </li>
              ))}
            </ol>
            <Text size="sm" tone="muted">
              {t('exam.intro_timer')}
            </Text>
            {results && (
              <div className="fluent-exam__intro-results">
                <Text size="sm" weight="bold">
                  {t('exam.intro_last', { score: results.last.score, max: EXAM_MAX_SCORE })} ·{' '}
                  {t('exam.intro_best', { score: results.best.score, max: EXAM_MAX_SCORE })}
                </Text>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShownResult(results.last);
                    setView('review');
                  }}
                >
                  {t('exam.intro_view_answers')}
                </Button>
              </div>
            )}
            <Button
              variant="primary"
              size="lg"
              fullWidth
              onClick={() => {
                setSittingXp(0);
                setView('running');
              }}
            >
              {results ? t('exam.intro_retake') : t('exam.intro_start')}
            </Button>
          </div>
        </Stack>
      </Container>
    </Screen>
  );
}

/* ------------------------------ running ------------------------------ */

interface RunningProps {
  test: ExamTest;
  initial: ExamAttempt;
  onSave: (attempt: ExamAttempt) => void;
  /** A task just got its last answer: record it (XP, streak, missions). */
  onTasksFinished?: (attemptId: string, tasks: { task: number; correct: number }[]) => void;
  onFinish: (result: ExamResult) => void;
  onExit: () => void;
}

function RunningExam({ test, initial, onSave, onTasksFinished, onFinish, onExit }: RunningProps) {
  const { t } = useTranslation();
  const [attempt, setAttempt] = useState<ExamAttempt>(initial);
  const [exitOpen, setExitOpen] = useState(false);
  const [finishOpen, setFinishOpen] = useState(false);
  const attemptRef = useRef(attempt);
  attemptRef.current = attempt;
  const finishedRef = useRef(false);

  /** Send every task that is finished but not yet sent, and remember it.
   *  Runs on every change, on opening (a sitting resumed after an update
   *  may already hold finished tasks) and on finishing. */
  const reconcile = (changed: ExamAttempt): ExamAttempt => {
    if (!onTasksFinished) return changed;
    const done = newlyFinishedTasks(test, changed);
    if (done.length === 0) return changed;
    const next = markReported(changed, done.map((d) => d.task));
    onTasksFinished(next.id, done);
    return next;
  };

  // Save the fresh sitting at once, so leaving before the first answer
  // still resumes the same clock.
  useEffect(() => {
    // attemptRef, not `initial`: in dev StrictMode this effect runs twice,
    // and the second run must see the tasks the first one already sent.
    const before = attemptRef.current;
    const next = reconcile(before);
    attemptRef.current = next;
    if (next !== before) setAttempt(next);
    onSave(next);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (changed: ExamAttempt) => {
    const next = reconcile(changed);
    attemptRef.current = next;
    setAttempt(next);
    onSave(next);
  };

  const finish = (elapsedMs: number, timedOut: boolean) => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    const last = reconcile({ ...attemptRef.current, elapsedMs });
    onFinish(finishAttempt(test, last, Date.now(), timedOut));
  };

  const elapsed = useExamClock({
    initialElapsedMs: initial.elapsedMs,
    running: true,
    onPersist: (ms) => {
      if (finishedRef.current) return;
      const next = { ...attemptRef.current, elapsedMs: ms };
      attemptRef.current = next;
      onSave(next);
    },
    onTimeUp: (ms) => finish(ms, true),
  });

  const left = remainingMs(elapsed);
  const task = EXAM_TASKS[attempt.taskIndex] ?? EXAM_TASKS[0];
  const goTo = (i: number) => {
    update({ ...attemptRef.current, taskIndex: i });
    window.scrollTo(0, 0);
  };
  const pick = (key: ExamTaskKey) => (index: number, letter: string) =>
    update(setAnswer(attemptRef.current, key, index, letter));
  const clear = (key: ExamTaskKey) => (index: number) => update(clearAnswer(attemptRef.current, key, index));
  const unanswered = unansweredTotal(attempt.answers);

  return (
    <Screen>
      <header className="fluent-exam__bar">
        <Button variant="ghost" size="sm" onClick={() => setExitOpen(true)} aria-label={t('exam.exit_aria')}>
          ✕
        </Button>
        <span
          className="fluent-exam__clock"
          data-warn={left <= DANGER_MS ? 'danger' : left <= WARN_MS ? 'warn' : undefined}
          role="timer"
          aria-label={t('exam.timer_aria', { time: formatClock(left) })}
        >
          {formatClock(left)}
        </span>
        <Button variant="primary" size="sm" onClick={() => setFinishOpen(true)}>
          {t('exam.finish')}
        </Button>
      </header>

      <TaskSteps
        current={attempt.taskIndex}
        answers={attempt.answers}
        onPick={goTo}
      />

      <Container size="sm">
        <Stack gap={6} style={{ padding: 'var(--space-4, 16px) 0 var(--space-16, 64px)' }}>
          <TaskView
            taskKey={task.key}
            test={test}
            answers={attempt.answers}
            onPick={pick}
            onClear={clear}
            listening={attempt.listening}
            onListening={(l: ListeningState) => update({ ...attemptRef.current, listening: l })}
            review={false}
          />
          <nav className="fluent-exam__pager">
            <Button
              variant="neutral"
              size="md"
              disabled={attempt.taskIndex === 0}
              onClick={() => goTo(attempt.taskIndex - 1)}
            >
              ← {t('exam.prev_task')}
            </Button>
            {attempt.taskIndex < EXAM_TASKS.length - 1 ? (
              <Button variant="secondary" size="md" onClick={() => goTo(attempt.taskIndex + 1)}>
                {t('exam.next_task')} →
              </Button>
            ) : (
              <Button variant="primary" size="md" onClick={() => setFinishOpen(true)}>
                {t('exam.finish')}
              </Button>
            )}
          </nav>
        </Stack>
      </Container>

      <Dialog
        open={exitOpen}
        onOpenChange={setExitOpen}
        title={t('exam.exit_title')}
        description={t('exam.exit_body')}
        actions={
          <Stack gap={2} align="stretch" style={{ width: '100%' }}>
            <Button variant="primary" fullWidth onClick={() => setExitOpen(false)}>
              {t('exam.exit_stay')}
            </Button>
            <Button variant="ghost" fullWidth onClick={onExit}>
              {t('exam.exit_confirm')}
            </Button>
          </Stack>
        }
      />
      <Dialog
        open={finishOpen}
        onOpenChange={setFinishOpen}
        title={t('exam.finish_title')}
        description={
          unanswered > 0 ? t('exam.finish_body_left', { count: unanswered }) : t('exam.finish_body_all')
        }
        actions={
          <Stack gap={2} align="stretch" style={{ width: '100%' }}>
            <Button variant="primary" fullWidth onClick={() => finish(elapsed, false)}>
              {t('exam.finish_confirm')}
            </Button>
            <Button variant="ghost" fullWidth onClick={() => setFinishOpen(false)}>
              {t('exam.finish_stay')}
            </Button>
          </Stack>
        }
      />
    </Screen>
  );
}

/* ------------------------------ shared ------------------------------ */

function TaskSteps({
  current,
  answers,
  onPick,
  review,
  result,
}: {
  current: number;
  answers: ExamAnswers;
  onPick: (i: number) => void;
  review?: boolean;
  result?: Record<ExamTaskKey, number>;
}) {
  const { t } = useTranslation();
  const { ref: stepsRef, bar } = useScrollBar<HTMLElement>();
  return (
    <div className="fluent-exam__steps-wrap">
      <nav ref={stepsRef} className="fluent-exam__steps" aria-label={t('exam.review_title')}>
        {EXAM_TASKS.map((task, i) => {
          const done = review && result ? result[task.key] : answeredCount(answers, task.key);
          return (
            <Pressable
              key={task.key}
              className="fluent-exam__step"
              data-current={i === current || undefined}
              data-complete={(!review && done === task.items) || undefined}
              onClick={() => onPick(i)}
              aria-current={i === current ? 'step' : undefined}
            >
              <span className="fluent-exam__step-n">{i + 1}</span>
              <span className="fluent-exam__step-name">{t(`exam.task_${i + 1}`)}</span>
              <span className="fluent-exam__step-count">
                {t('exam.answered', { done, total: task.items })}
              </span>
            </Pressable>
          );
        })}
      </nav>
      {bar && (
        <div className="fluent-exam__steps-bar" aria-hidden="true">
          <span style={{ width: `${bar.size * 100}%`, left: `${bar.start * 100}%` }} />
        </div>
      )}
    </div>
  );
}

/**
 * An always-visible scroll bar for a sideways row. Phones and macOS hide
 * native scrollbars until you scroll, so on a narrow screen nothing said
 * tasks 4–6 were off to the right (Tornike 2026-09-26). `bar` is null when
 * everything fits; `size` and `start` are fractions of the track.
 */
function useScrollBar<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [bar, setBar] = useState<{ size: number; start: number } | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const { scrollWidth, clientWidth, scrollLeft } = el;
      if (scrollWidth <= clientWidth + 1) {
        setBar(null);
        return;
      }
      setBar({ size: clientWidth / scrollWidth, start: scrollLeft / scrollWidth });
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, []);
  return { ref, bar };
}

function TaskView({
  taskKey,
  test,
  answers,
  onPick,
  onClear,
  listening,
  onListening,
  review,
}: {
  taskKey: ExamTaskKey;
  test: ExamTest;
  answers: ExamAnswers;
  onPick: (key: ExamTaskKey) => (index: number, letter: string) => void;
  onClear: (key: ExamTaskKey) => (index: number) => void;
  listening: ListeningState;
  onListening: (l: ListeningState) => void;
  review: boolean;
}) {
  switch (taskKey) {
    case 'listening':
      return (
        <ListeningTask
          task={test.listening}
          answers={answers.listening}
          onPick={onPick('listening')}
          state={listening}
          onState={onListening}
          review={review}
        />
      );
    case 'matching':
      return (
        <MatchingTask
          task={test.matching}
          answers={answers.matching}
          onPick={onPick('matching')}
          onClear={onClear('matching')}
          review={review}
        />
      );
    case 'reading':
      return (
        <ReadingTask
          task={test.reading}
          answers={answers.reading}
          onPick={onPick('reading')}
          onClear={onClear('reading')}
          review={review}
        />
      );
    case 'vocabulary':
      return (
        <VocabularyTask
          task={test.vocabulary}
          answers={answers.vocabulary}
          onPick={onPick('vocabulary')}
          onClear={onClear('vocabulary')}
          review={review}
        />
      );
    case 'grammar':
      return (
        <GrammarTask
          task={test.grammar}
          answers={answers.grammar}
          onPick={onPick('grammar')}
          onClear={onClear('grammar')}
          review={review}
        />
      );
    case 'dialogue':
      return (
        <DialogueTask
          task={test.dialogue}
          answers={answers.dialogue}
          onPick={onPick('dialogue')}
          onClear={onClear('dialogue')}
          review={review}
        />
      );
  }
}

/* ------------------------------ results ------------------------------ */

function ExamResults({
  n,
  result,
  xp,
  streakMilestone,
  onReview,
  onRetake,
  onBack,
}: {
  n: number;
  result: ExamResult;
  /** XP paid for the tasks finished in this sitting. */
  xp: number;
  /** A streak milestone this sitting reached, or null. */
  streakMilestone: number | null;
  onReview: () => void;
  onRetake: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const pct = Math.round((result.score / EXAM_MAX_SCORE) * 100);
  return (
    <Screen>
      <Container size="sm">
        <Stack gap={6} style={{ padding: 'var(--space-8, 32px) 0 var(--space-16, 64px)' }}>
          <Stack gap={2} align="center" style={{ textAlign: 'center' }}>
            <Heading level={2}>{t('exam.results_title', { n })}</Heading>
            {result.timedOut && <Text tone="muted">{t('exam.results_timed_out')}</Text>}
          </Stack>

          <div
            className="fluent-exam__score"
            role="img"
            aria-label={t('exam.results_score_aria', { score: result.score, max: EXAM_MAX_SCORE })}
            style={{ ['--pct' as string]: `${pct}` }}
          >
            <span className="fluent-exam__score-n">{result.score}</span>
            <span className="fluent-exam__score-max">/ {EXAM_MAX_SCORE}</span>
            <span className="fluent-exam__score-pct">{pct}%</span>
          </div>
          <Text size="sm" tone="muted" align="center">
            {t('exam.results_time', { time: formatClock(result.elapsedMs) })}
          </Text>
          {(xp > 0 || streakMilestone !== null) && (
            <Stack gap={1} align="center" style={{ textAlign: 'center' }}>
              {xp > 0 && (
                <Text weight="bold" tone="primary">
                  {t('exam.results_xp', { xp })}
                </Text>
              )}
              {streakMilestone !== null && (
                <Text weight="bold">{t('lesson.complete.streak_milestone_title', { days: streakMilestone })}</Text>
              )}
            </Stack>
          )}

          <div className="fluent-exam__breakdown">
            {EXAM_TASKS.map((task, i) => (
              <div key={task.key} className="fluent-exam__breakdown-row">
                <span className="fluent-exam__breakdown-name">
                  {i + 1}. {t(`exam.task_${i + 1}`)}
                </span>
                <ProgressBar value={result.byTask[task.key] / task.items} tone="primary" size="sm" />
                <span className="fluent-exam__breakdown-n">
                  {result.byTask[task.key]}/{task.items}
                </span>
              </div>
            ))}
          </div>

          <Stack gap={3}>
            <Button variant="primary" size="lg" fullWidth onClick={onReview}>
              {t('exam.results_review')}
            </Button>
            <Button variant="secondary" size="md" fullWidth onClick={onRetake}>
              {t('exam.results_retake')}
            </Button>
            <Button variant="ghost" size="md" fullWidth onClick={onBack}>
              {t('exam.back_to_tests')}
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Screen>
  );
}

/* ------------------------------ review ------------------------------ */

function ExamReview({ test, answers, onClose }: { test: ExamTest; answers: ExamAnswers; onClose: () => void }) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const task = EXAM_TASKS[index] ?? EXAM_TASKS[0];
  const noop = () => () => undefined;
  const { byTask } = scoreAnswers(test, answers);

  return (
    <Screen>
      <header className="fluent-exam__bar">
        <Button variant="ghost" size="sm" onClick={onClose} aria-label={t('exam.review_close')}>
          ✕
        </Button>
        <span className="fluent-exam__bar-title">{t('exam.review_title')}</span>
        <span />
      </header>
      <TaskSteps
        current={index}
        answers={answers}
        onPick={(i) => {
          setIndex(i);
          window.scrollTo(0, 0);
        }}
        review
        result={byTask}
      />
      <Container size="sm">
        <Stack gap={6} style={{ padding: 'var(--space-4, 16px) 0 var(--space-16, 64px)' }}>
          <TaskView
            taskKey={task.key}
            test={test}
            answers={answers}
            onPick={noop}
            onClear={noop}
            listening={{ previewLeftMs: 0, playsUsed: 2 }}
            onListening={() => undefined}
            review
          />
          <nav className="fluent-exam__pager">
            <Button variant="neutral" size="md" disabled={index === 0} onClick={() => setIndex(index - 1)}>
              ← {t('exam.prev_task')}
            </Button>
            {index < EXAM_TASKS.length - 1 ? (
              <Button variant="secondary" size="md" onClick={() => setIndex(index + 1)}>
                {t('exam.next_task')} →
              </Button>
            ) : (
              <Button variant="primary" size="md" onClick={onClose}>
                {t('exam.review_close')}
              </Button>
            )}
          </nav>
        </Stack>
      </Container>
    </Screen>
  );
}
